import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { VaultStateService } from './vault-state.service';
import { CryptoEngineService } from './crypto-engine.service';
import { CryptoInactivityService } from './crypto-inactivity.service';
import { UserPreferenceService } from '../user-preference/user-preference.service';
import { filter, take, combineLatest } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CryptoService {
  private http = inject(HttpClient);
  private vaultState = inject(VaultStateService);
  private cryptoEngine = inject(CryptoEngineService);
  private inactivityService = inject(CryptoInactivityService);
  private apiUrl = environment.apiBaseUrl;

  public locked$ = this.vaultState.locked$;
  public showVaultSetup$ = this.vaultState.showVaultSetup$;

  private encryptedSearchFields: string[] = [];

  private readonly EXCLUDED_KEYS = [
    'id',
    '_id',
    'patientId',
    'gender',
    'bloodType',
    'status',
    'createdAt',
    'updatedAt',
    'isClientEncrypted',
    'patientVisit',
    'appointmentId',
    'visitId',
    'vitals',
    'followUpDate',
    'serviceId',
    'clinicalId',
  ];

  constructor() {
  }

  // --- Mnemonic & Setup ---

  async generateMnemonic(): Promise<string> {
    return this.cryptoEngine.generateMnemonic();
  }

  async verifyMnemonic(mnemonic: string): Promise<void> {
    const keys = await this.cryptoEngine.verifyMnemonic(mnemonic);
    await this.cryptoEngine.importMasterKey(keys.masterKeyBytes, false);
    await this.cryptoEngine.importSearchSalt(keys.searchSaltBytes);
  }

  // --- Blind Indexing ---

  async generateBlindIndex(data: string): Promise<string> {
    await this.ensureKey();
    if (this.vaultState.isBypassVault && !this.vaultState.searchSalt) {
      return data;
    }
    if (!this.vaultState.searchSalt) {
      throw new Error('Vault is locked or search salt not initialized');
    }
    return this.cryptoEngine.generateBlindIndex(
      data,
      this.vaultState.searchSalt
    );
  }

  // --- Vault Verification (Organization Tag) ---

  createVerificationTag(mnemonic: string): Promise<string> {
    return this.vaultState.createVerificationTag(mnemonic);
  }

  verifyExistingTag(mnemonic: string, encryptedTag: string): Promise<boolean> {
    return this.vaultState.verifyExistingTag(mnemonic, encryptedTag);
  }

  setupVault(mnemonic: string, pin: string): Promise<void> {
    const res = this.vaultState.setupVault(mnemonic, pin);
    this.inactivityService.resetAutoLock();
    return res;
  }

  // --- Unlock & Restore ---

  changePin(oldPin: string, newPin: string): Promise<boolean> {
    return this.vaultState.changePin(oldPin, newPin);
  }

  isVaultInitialized(): Promise<boolean> {
    return this.vaultState.isVaultInitialized();
  }

  async unlockVault(pin: string): Promise<boolean> {
    const success = await this.vaultState.unlockVault(pin);
    if (success) {
      this.inactivityService.resetAutoLock();
    }
    return success;
  }

  resetVault(): Promise<void> {
    return this.vaultState.resetVault();
  }

  lockVault(showLockUI: boolean = true): void {
    this.vaultState.lockVault(showLockUI);
  }

  checkInitialLockStatus(): Promise<void> {
    return this.isVaultInitialized().then(() => {});
  }

  triggerVaultSetup() {
    this.vaultState.triggerVaultSetup();
  }

  closeVaultSetup() {
    this.vaultState.closeVaultSetup();
  }

  // --- Encryption / Decryption ---

  private async ensureKey(): Promise<void> {
    if (this.vaultState.masterKey && this.vaultState.searchSalt) return;

    if (this.vaultState.isBypassVault) {
      return;
    }

    const initialized = await this.isVaultInitialized();
    if (!initialized) {
      this.triggerVaultSetup();
      throw new Error('Vault setup required');
    }

    this.vaultState.setLockedState(true);

    return new Promise<void>((resolve) => {
      const sub = this.locked$.subscribe((locked) => {
        if (
          !locked &&
          this.vaultState.masterKey &&
          this.vaultState.searchSalt
        ) {
          sub.unsubscribe();
          resolve();
        }
      });
    });
  }

  async encrypt(data: string): Promise<string> {
    await this.ensureKey();
    if (this.vaultState.isBypassVault && !this.vaultState.masterKey) {
      return data;
    }
    return this.cryptoEngine.encrypt(data, this.vaultState.masterKey!);
  }

  async decrypt(cipherBase64: string): Promise<string> {
    await this.ensureKey();
    if (this.vaultState.isBypassVault && !this.vaultState.masterKey) {
      return cipherBase64; // Caller (processPayload) handles placeholder per-key
    }
    return this.cryptoEngine.decrypt(cipherBase64, this.vaultState.masterKey!);
  }

  /**
   * Returns a realistic-looking redacted placeholder for a given field key
   * when the vault is bypassed (doorway admin context).
   */
  getBypassPlaceholder(key: string): string {
    const k = key.toLowerCase();

    // Name fields
    if (k === 'firstname' || k === 'first_name')    return 'John';
    if (k === 'lastname'  || k === 'last_name')     return 'D.';
    if (k === 'fullname'  || k === 'full_name' || k === 'name') return 'John D.';
    if (k === 'middlename' || k === 'middle_name')  return 'M.';
    if (k === 'displayname' || k === 'display_name') return 'John D.';

    // Contact fields
    if (k.includes('email'))                        return 'patient@private.com';
    if (k.includes('phone') || k.includes('mobile') || k.includes('tel'))
                                                    return '•••• ••• 0000';
    if (k.includes('fax'))                          return '•••• ••• 0001';

    // Address fields
    if (k.includes('street') || k === 'address1' || k === 'address2')
                                                    return '•• Private Street';
    if (k === 'city')                               return 'City';
    if (k === 'state' || k === 'province')          return 'State';
    if (k === 'zip' || k === 'postalcode' || k === 'postal_code')
                                                    return '••••••';
    if (k === 'country')                            return 'Country';

    // Identity / medical
    if (k.includes('ssn') || k.includes('national') || k.includes('nic'))
                                                    return '••• – •• – ••••';
    if (k.includes('passport'))                     return 'XX••••••';
    if (k.includes('license'))                      return '••••••••';
    if (k.includes('dob') || k === 'dateofbirth' || k === 'birthdate')
                                                    return '••/••/••••';

    // Financial
    if (k.includes('account') || k.includes('iban')) return '•••• •••• •••• 0000';
    if (k.includes('card'))                         return '•••• •••• •••• 0000';
    if (k.includes('cvv') || k.includes('cvc'))     return '•••';

    // Notes / description
    if (k.includes('note') || k.includes('comment') || k.includes('remark'))
                                                    return '[Protected note]';
    if (k.includes('description') || k.includes('summary'))
                                                    return '[Protected description]';
    if (k.includes('diagnosis') || k.includes('complaint'))
                                                    return '[Protected clinical data]';
    if (k.includes('prescription') || k.includes('medication'))
                                                    return '[Protected prescription]';

    // Generic fallback
    return '[Protected]';
  }

  async encryptPayload(data: any, searchableFields?: string[]): Promise<any> {
    if (!data) return data;
    await this.ensureKey();
    const fieldsToBlindIndex = [
      ...this.encryptedSearchFields,
      ...(searchableFields || []),
    ];
    const uniqueFields = [...new Set(fieldsToBlindIndex)];
    return this.processPayload(data, true, uniqueFields);
  }

  setEncryptedSearchFields(fields: string[]) {
    this.encryptedSearchFields = fields || [];
  }

  async decryptPayload(data: any): Promise<any> {
    if (!data) return data;
    await this.ensureKey();
    return this.processPayload(data, false);
  }

  private async processPayload(
    data: any,
    isEncrypt: boolean,
    searchableFields: string[] = []
  ): Promise<any> {
    if (Array.isArray(data)) {
      return Promise.all(
        data.map((item) =>
          this.processPayload(item, isEncrypt, searchableFields)
        )
      );
    } else if (typeof data === 'object' && data !== null) {
      if (data instanceof Date) return data.toISOString();

      const result: any = {};
      for (const key of Object.keys(data)) {
        let value = data[key];
        if (value instanceof Date) value = value.toISOString();

        if (this.EXCLUDED_KEYS.includes(key)) {
          result[key] = value;
        } else if (typeof value === 'string') {
          if (isEncrypt && searchableFields.includes(key)) {
            try {
              const blindIndex = await this.generateBlindIndex(value);
              result[`${key}_index`] = blindIndex;
            } catch (e) {
              console.warn(`Failed to generate blind index for ${key}`, e);
            }
          }
          try {
            if (isEncrypt) {
              result[key] = await this.encrypt(value);
            } else {
              // When vault is bypassed (doorway mode) — show context-aware placeholder
              if (this.vaultState.isBypassVault && !this.vaultState.masterKey) {
                result[key] = this.getBypassPlaceholder(key);
              } else if (value.includes(' ')) {
                // Check if string contains multiple encrypted parts (separated by space)
                const parts = value.split(' ');
                const decryptedParts = await Promise.all(
                  parts.map(async (part) => {
                    if (part.length < 20) return part; // Optimization: too short to be our AES-GCM base64
                    try {
                      return await this.decrypt(part);
                    } catch (e) {
                      return part;
                    }
                  })
                );
                result[key] = decryptedParts.join(' ');
              } else {
                result[key] = await this.decrypt(value);
              }
            }
          } catch (e) {
            if (!isEncrypt) result[key] = value;
            else throw e;
          }
        } else {
          result[key] = await this.processPayload(
            value,
            isEncrypt,
            searchableFields
          );
        }
      }
      return result;
    }
    return data;
  }

  // --- Settings ---

  private userPreference = inject(UserPreferenceService);

  loadSecuritySettings() {
    combineLatest([
      this.userPreference.selectedOrg$.pipe(filter((org) => !!org)),
      this.userPreference.selectedBranch$.pipe(filter((branch) => !!branch)),
    ])
      .pipe(
        take(1)
      )
      .subscribe(([org, branch]) => {
        this.http
          .get<any>(`${this.apiUrl}settings/security/public`, {
            withCredentials: true,
          })
          .subscribe({
            next: (settings) => {
              if (settings) {
                if (settings.encryptedSearchFields) {
                  this.setEncryptedSearchFields(settings.encryptedSearchFields);
                }
                if (settings.patientDataLockoutInMinutes) {
                  this.updateLockoutTime(settings.patientDataLockoutInMinutes);
                }
              }
            },
            error: (err) => console.warn('Failed to fetch security settings', err),
          });
      });
  }

  updateLockoutTime(minutes: number) {
    this.inactivityService.updateLockoutTime(minutes);
  }
}
